import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import DragDropGame from '@/components/DragDropGame';
import PracticeExitControls from '@/components/PracticeExitControls';
import ConceptTeachCard from '@/components/ConceptTeachCard';
import {
  ArrowLeft, Volume2, Mic, MicOff, Check, X, Star, RefreshCw,
  ArrowRight, Loader2, Trophy, Home
} from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import { useReadSpeed, useKaraoke, splitSentences, splitWords, SPEED_PRESETS } from '@/hooks/useReadSpeed';
import { generateKmathQuestions, isKmathActivity } from '@/utils/kmathQuestions';
import { Turtle, Rabbit, Zap, VolumeX } from 'lucide-react';

const ActivityPage = () => {
  const { activityId } = useParams();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('child');
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [activity, setActivity] = useState(null);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [startTime] = useState(Date.now());
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  // Zearn-style phase panel — only active for Kiddom Math (`kmath-*`) activities.
  const isKmath = String(activityId || '').startsWith('kmath-');
  const [zearnFlow, setZearnFlow] = useState(null);
  const [zearnPhase, setZearnPhase] = useState('independent'); // 'learn' | 'fluency' | 'independent'
  const [fluencyRemaining, setFluencyRemaining] = useState(60);
  const fluencyTimerRef = useRef(null);
  const fluencyLoggedRef = useRef(false);

  // Karaoke read-aloud for Kiddom Learn-phase concept text — mirrors Summer Slide
  // and SEL. Remembers the child's chosen speed via useReadSpeed.
  const { stop: stopKmathTTS, isSpeaking: isKmathReading, speakChunks: kmathSpeakChunks } =
    useElevenLabsTTS({ voice: 'shimmer', speed: 0.95 });
  const { readSpeed: kmathReadSpeed, cycleReadSpeed: cycleKmathSpeed } = useReadSpeed(childId);
  const {
    highlightedSentence: kmathSentIdx,
    highlightedWord: kmathWordIdx,
    playPassage: playKmathConcept,
    stopPassage: stopKmathConcept,
  } = useKaraoke({
    speakChunks: kmathSpeakChunks,
    stopStory: stopKmathTTS,
    isReading: isKmathReading,
    readSpeed: kmathReadSpeed,
  });
  
  const recognitionRef = useRef(null);

  // Randomized question pool generator
  const shuffleArray = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const pickRandom = (pool, count) => shuffleArray(pool).slice(0, count);

  // Large question pools for variety
  const questionPools = {
    alphabets: [
      { question: 'Which one is the letter A?', options: ['🅰️ A', '🅱️ B', 'C', 'D'], answer: '🅰️ A', hint: 'The very first letter!', visual: '🔤' },
      { question: 'What letter comes after B?', options: ['A', 'C', 'D', 'E'], answer: 'C', hint: 'A, B, ... what comes next?', visual: '🔤' },
      { question: 'Which letter starts the word "Dog"?', options: ['B', 'C', 'D', 'G'], answer: 'D', hint: '🐕 Dog!', visual: '🐕' },
      { question: 'Find the letter that starts "Fish"', options: ['E', 'F', 'G', 'H'], answer: 'F', hint: '🐟 Fish!', visual: '🐟' },
      { question: 'What letter does "Sun" start with?', options: ['R', 'S', 'T', 'U'], answer: 'S', hint: '☀️ Sun!', visual: '☀️' },
      { question: 'Which letter starts "Moon"?', options: ['L', 'M', 'N', 'O'], answer: 'M', hint: '🌙 Moon!', visual: '🌙' },
      { question: 'What letter does "Kite" begin with?', options: ['J', 'K', 'L', 'M'], answer: 'K', hint: '🪁 Kite!', visual: '🪁' },
      { question: 'What is the last letter of the alphabet?', options: ['X', 'Y', 'Z', 'W'], answer: 'Z', hint: '...X, Y, ...', visual: '🔤' },
      { question: 'Which letter looks like a circle?', options: ['O', 'I', 'L', 'T'], answer: 'O', hint: 'Round and round!', visual: '⭕' },
      { question: 'Find the letter in "Hat"', options: ['G', 'H', 'J', 'K'], answer: 'H', hint: '🎩 Hat!', visual: '🎩' },
      { question: 'What letter starts "Ball"?', options: ['A', 'B', 'C', 'D'], answer: 'B', hint: '⚽ Ball!', visual: '⚽' },
      { question: 'Which letter begins "Lion"?', options: ['K', 'L', 'M', 'N'], answer: 'L', hint: '🦁 Lion!', visual: '🦁' },
    ],
    counting: [
      { question: 'Count the stars! ⭐⭐⭐', options: ['2', '3', '4', '5'], answer: '3', hint: 'Count each one!', visual: '⭐' },
      { question: 'How many apples? 🍎🍎🍎🍎', options: ['3', '4', '5', '6'], answer: '4', hint: 'Count carefully!', visual: '🍎' },
      { question: 'Count the hearts! ❤️❤️', options: ['1', '2', '3', '4'], answer: '2', hint: 'Not too many!', visual: '❤️' },
      { question: 'How many fish? 🐟🐟🐟🐟🐟', options: ['4', '5', '6', '7'], answer: '5', hint: 'Like your fingers!', visual: '🐟' },
      { question: 'Count them! 🌸🌸🌸🌸🌸🌸', options: ['5', '6', '7', '8'], answer: '6', hint: 'One more than 5!', visual: '🌸' },
      { question: 'What comes after 3?', options: ['2', '3', '4', '5'], answer: '4', hint: '1, 2, 3, ...?', visual: '🔢' },
      { question: 'What number comes before 5?', options: ['3', '4', '5', '6'], answer: '4', hint: '..., 4, 5!', visual: '🔢' },
      { question: 'Count the butterflies! 🦋🦋🦋🦋🦋🦋🦋', options: ['5', '6', '7', '8'], answer: '7', hint: 'Count slowly!', visual: '🦋' },
      { question: 'How many moons? 🌙', options: ['1', '2', '3', '0'], answer: '1', hint: 'Just one!', visual: '🌙' },
      { question: 'Count: 🎈🎈🎈🎈🎈🎈🎈🎈', options: ['6', '7', '8', '9'], answer: '8', hint: 'Keep counting!', visual: '🎈' },
    ],
    reading: [
      { question: 'What rhymes with "cat"?', options: ['🐕 dog', '🎩 hat', '☀️ sun', '🏃 run'], answer: '🎩 hat', hint: 'Same ending sound!', visual: '🐱' },
      { question: 'What rhymes with "moon"?', options: ['⭐ star', '🥄 spoon', '🐟 fish', '☀️ sun'], answer: '🥄 spoon', hint: '-oon sounds!', visual: '🌙' },
      { question: 'What rhymes with "tree"?', options: ['🍃 leaf', '🐝 bee', '🐦 bird', '💚 green'], answer: '🐝 bee', hint: '-ee sounds!', visual: '🌳' },
      { question: 'Which word rhymes with "bed"?', options: ['🔴 red', '🔵 blue', '💚 green', '💛 yellow'], answer: '🔴 red', hint: '-ed sounds!', visual: '🛏️' },
      { question: 'What rhymes with "cake"?', options: ['🌊 lake', '🌸 rose', '🌙 night', '🍕 food'], answer: '🌊 lake', hint: '-ake sounds!', visual: '🎂' },
      { question: 'Find the word that rhymes with "ball"', options: ['🐕 dog', '🏔️ tall', '🐟 fish', '🦁 lion'], answer: '🏔️ tall', hint: '-all sounds!', visual: '⚽' },
      { question: 'Which word starts like "snake"?', options: ['🌟 star', '🐱 cat', '🐕 dog', '🐟 fish'], answer: '🌟 star', hint: 'Sss sounds!', visual: '🐍' },
      { question: 'What starts with the same sound as "bear"?', options: ['🎈 balloon', '🐱 cat', '🐕 dog', '🌸 flower'], answer: '🎈 balloon', hint: 'Buh sounds!', visual: '🐻' },
    ],
    writing: [
      { question: 'Which letter has NO curves?', options: ['O', 'A', 'S', 'C'], answer: 'A', hint: 'All straight lines!', visual: '✏️' },
      { question: 'Which letter is just one circle?', options: ['O', 'B', 'D', 'Q'], answer: 'O', hint: 'Round and round!', visual: '⭕' },
      { question: 'Which is the tallest letter?', options: ['a', 'b', 'c', 'e'], answer: 'b', hint: 'Goes up high!', visual: '📏' },
      { question: 'Which letter looks like a zigzag?', options: ['Z', 'S', 'O', 'C'], answer: 'Z', hint: 'Zig zag!', visual: '⚡' },
      { question: 'Which letter looks like a snake?', options: ['I', 'L', 'S', 'T'], answer: 'S', hint: 'Ssslithery!', visual: '🐍' },
      { question: 'How many lines in the letter T?', options: ['1', '2', '3', '4'], answer: '2', hint: 'One across, one down!', visual: '✏️' },
    ],
    math: [
      { question: '🍎🍎 + 🍎🍎 = ?', options: ['3', '4', '5', '6'], answer: '4', hint: 'Count all the apples!', visual: '🍎' },
      { question: '⭐⭐⭐ + ⭐⭐ = ?', options: ['4', '5', '6', '7'], answer: '5', hint: 'Count all the stars!', visual: '⭐' },
      { question: '🐟 + 🐟🐟🐟🐟 = ?', options: ['3', '4', '5', '6'], answer: '5', hint: 'Count all the fish!', visual: '🐟' },
      { question: '🌸🌸🌸 - 🌸 = ?', options: ['1', '2', '3', '4'], answer: '2', hint: 'Take one away!', visual: '🌸' },
      { question: '❤️❤️❤️❤️❤️ - ❤️❤️ = ?', options: ['2', '3', '4', '5'], answer: '3', hint: 'Take two away!', visual: '❤️' },
      { question: 'Which is more: 🐱🐱🐱 or 🐕🐕?', options: ['🐱 Cats', '🐕 Dogs', 'Same'], answer: '🐱 Cats', hint: 'Count each group!', visual: '🐱' },
      { question: 'What is 1 + 1?', options: ['1', '2', '3', '0'], answer: '2', hint: 'One plus one more!', visual: '🔢' },
      { question: '🎈🎈🎈 + 🎈 = ?', options: ['3', '4', '5', '2'], answer: '4', hint: 'Count all balloons!', visual: '🎈' },
      { question: 'Which number is bigger: 3 or 7?', options: ['3', '7', 'Same'], answer: '7', hint: 'Which is more?', visual: '📏' },
      { question: '10 - 5 = ?', options: ['3', '4', '5', '6'], answer: '5', hint: 'Take 5 away from 10!', visual: '🔢' },
    ],
    science: [
      { question: 'What do plants need to grow?', options: ['🎵 Music', '💧 Water', '🧸 Toys', '📚 Books'], answer: '💧 Water', hint: 'Plants get thirsty!', visual: '🌱' },
      { question: 'What animal says "Moo"?', options: ['🐕 Dog', '🐱 Cat', '🐄 Cow', '🐦 Bird'], answer: '🐄 Cow', hint: 'Found on farms!', visual: '🐄' },
      { question: 'What color is a banana?', options: ['🔴 Red', '🔵 Blue', '💛 Yellow', '💚 Green'], answer: '💛 Yellow', hint: 'Bright and sunny!', visual: '🍌' },
      { question: 'Which animal lives in water?', options: ['🐱 Cat', '🐕 Dog', '🐟 Fish', '🐦 Bird'], answer: '🐟 Fish', hint: 'It swims!', visual: '🌊' },
      { question: 'What gives us light during the day?', options: ['🌙 Moon', '☀️ Sun', '⭐ Stars', '💡 Lamp'], answer: '☀️ Sun', hint: 'Big and bright!', visual: '☀️' },
      { question: 'How many legs does a cat have?', options: ['2', '4', '6', '8'], answer: '4', hint: 'Count them!', visual: '🐱' },
      { question: 'What season has snow?', options: ['☀️ Summer', '🌸 Spring', '❄️ Winter', '🍂 Fall'], answer: '❄️ Winter', hint: 'Brrr, cold!', visual: '❄️' },
      { question: 'Which is a fruit?', options: ['🥕 Carrot', '🍎 Apple', '🥦 Broccoli', '🥬 Lettuce'], answer: '🍎 Apple', hint: 'Sweet and crunchy!', visual: '🍎' },
    ],
  };

  // Build sample activities with randomized questions from pools
  const sampleActivities = {
    'alphabets-1': {
      id: 'alphabets-1', title: 'Meet the Letters!', subject: 'alphabets',
      questions: pickRandom(questionPools.alphabets, 5)
    },
    'counting-1': {
      id: 'counting-1', title: 'Count with Me!', subject: 'counting',
      questions: pickRandom(questionPools.counting, 5)
    },
    'reading-1': {
      id: 'reading-1', title: 'Rhyme Time!', subject: 'reading',
      questions: pickRandom(questionPools.reading, 5)
    },
    'writing-1': {
      id: 'writing-1', title: 'Letter Shapes', subject: 'writing',
      questions: pickRandom(questionPools.writing, 4)
    },
    'math-1': {
      id: 'math-1', title: 'Math Fun!', subject: 'math',
      questions: pickRandom(questionPools.math, 5)
    },
    'science-1': {
      id: 'science-1', title: 'Nature Explorer', subject: 'science',
      questions: pickRandom(questionPools.science, 5)
    },
  };

  useEffect(() => {
    fetchData();
    initSpeechRecognition();
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [activityId, childId]);

  // Fetch Zearn-style flow metadata for Kiddom Math activities.
  useEffect(() => {
    if (!isKmath) return;
    let ignore = false;
    api.get(`/skills/zearn-flow/${activityId}`)
      .then((r) => { if (!ignore) setZearnFlow(r.data); })
      .catch(() => { /* optional — page still works without zearn-flow data */ });
    return () => { ignore = true; };
  }, [activityId, isKmath, api]);

  // Fluency countdown timer
  useEffect(() => {
    if (zearnPhase !== 'fluency') {
      if (fluencyTimerRef.current) clearInterval(fluencyTimerRef.current);
      return;
    }
    // Reset guard whenever a fresh fluency phase begins
    fluencyLoggedRef.current = false;
    setFluencyRemaining(60);
    fluencyTimerRef.current = setInterval(() => {
      setFluencyRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(fluencyTimerRef.current);
          toast.info("Fluency time's up! Great warm-up.");
          // Log the fluency session to backend (fire-and-forget) exactly once per
          // fluency phase entry. Guard prevents double-post from React state updates.
          if (childId && activityId && !fluencyLoggedRef.current) {
            fluencyLoggedRef.current = true;
            api.post('/skills/zearn-flow/fluency-session', {
              child_id: childId,
              activity_id: activityId,
              duration_seconds: 60,
              correct_count: score,
              attempted_count: answers.length,
              completed_via: 'timer_expired',
            }).then((r) => {
              if (r?.data?.xp_earned) {
                toast.success(`+${r.data.xp_earned} XP for finishing Fluency!`);
              }
            }).catch(() => { /* non-blocking */ });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (fluencyTimerRef.current) clearInterval(fluencyTimerRef.current); };
  }, [zearnPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      // Fetch child data
      if (childId) {
        const childRes = await api.get(`/children/${childId}`);
        setChild(childRes.data);
      }
      
      // Try to fetch activity from API
      try {
        const activityRes = await api.get(`/activities/${activityId}`);
        let activityData = activityRes.data;

        // Kiddom Math activities must get FRESH random questions each open — otherwise
        // kids see the same 3 problems every visit. Regenerate procedurally per session.
        if (isKmathActivity(activityId)) {
          activityData.questions = generateKmathQuestions(activityData, 5);
        }
        // Parse questions from content if not present at top level
        else if (!activityData.questions && activityData.content) {
          // Extract questions from content.questions if available
          if (activityData.content.questions) {
            activityData.questions = activityData.content.questions;
          } else {
            // Generate questions based on activity type and content
            activityData.questions = generateQuestionsFromContent(activityData);
          }
        }

        setActivity(activityData);
      } catch {
        // Use sample activity
        const sample = sampleActivities[activityId];
        if (sample) {
          setActivity(sample);
        } else {
          // Default to alphabets
          setActivity(sampleActivities['alphabets-1']);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Use sample activity
      setActivity(sampleActivities['alphabets-1']);
    } finally {
      setLoading(false);
    }
  };

  // Generate questions dynamically based on activity content
  const generateQuestionsFromContent = (activity) => {
    const subject = activity.subject;
    const content = activity.content || {};
    const questions = [];
    
    // Music activities
    if (subject === 'music') {
      if (content.type === 'pitch') {
        questions.push(
          { question: 'Is this sound HIGH or LOW?', options: ['High', 'Low'], answer: 'High', hint: 'High sounds are like a bird!' },
          { question: 'Which instrument makes a high sound?', options: ['Flute', 'Drum', 'Bass'], answer: 'Flute', hint: 'Small instruments make high sounds' },
          { question: 'Is a deep voice HIGH or LOW?', options: ['High', 'Low'], answer: 'Low', hint: 'Think of a giant!' }
        );
      } else if (content.type === 'tempo') {
        questions.push(
          { question: 'A rabbit runs FAST or SLOW?', options: ['Fast', 'Slow'], answer: 'Fast', hint: 'Rabbits hop quickly!' },
          { question: 'A turtle moves FAST or SLOW?', options: ['Fast', 'Slow'], answer: 'Slow', hint: 'Turtles take their time' },
          { question: 'Which is faster - walking or running?', options: ['Walking', 'Running'], answer: 'Running', hint: 'Think about a race!' }
        );
      } else {
        questions.push(
          { question: 'What do we use to make music?', options: ['Instruments', 'Toys', 'Books'], answer: 'Instruments', hint: 'Piano, guitar, drums...' },
          { question: 'How many beats in 4/4 time?', options: ['2', '4', '6'], answer: '4', hint: 'Count: 1, 2, 3, 4' },
          { question: 'What is a song made of?', options: ['Notes', 'Letters', 'Numbers'], answer: 'Notes', hint: 'Do, Re, Mi...' }
        );
      }
    }
    
    // PE activities
    else if (subject === 'pe') {
      if (content.type === 'locomotor') {
        questions.push(
          { question: 'Which is NOT a way to move?', options: ['Walking', 'Running', 'Sleeping', 'Skipping'], answer: 'Sleeping', hint: 'You stay still when sleeping!' },
          { question: 'Hopping uses how many feet?', options: ['One', 'Two', 'Three'], answer: 'One', hint: 'Like a bunny on one foot!' },
          { question: 'Galloping is like a...', options: ['Horse', 'Fish', 'Bird'], answer: 'Horse', hint: 'Clip-clop, clip-clop!' }
        );
      } else if (content.type === 'balance') {
        questions.push(
          { question: 'What helps you balance?', options: ['Your core', 'Your ears', 'Your nose'], answer: 'Your core', hint: 'Strong tummy muscles!' },
          { question: 'Stand on one foot for balance is...', options: ['Easy', 'A challenge', 'Impossible'], answer: 'A challenge', hint: 'Practice makes perfect!' },
          { question: 'Looking at one spot helps you...', options: ['Balance', 'Run', 'Jump'], answer: 'Balance', hint: 'Keep your eyes still!' }
        );
      } else {
        questions.push(
          { question: 'Exercise is good for your...', options: ['Body', 'Homework', 'TV'], answer: 'Body', hint: 'Strong and healthy!' },
          { question: 'What makes your heart beat faster?', options: ['Running', 'Sleeping', 'Reading'], answer: 'Running', hint: 'Moving makes your heart work!' },
          { question: 'Stretching helps your...', options: ['Muscles', 'Hair', 'Teeth'], answer: 'Muscles', hint: 'Flexible and loose!' }
        );
      }
    }
    
    // Sight words activities
    else if (subject === 'sight_words') {
      const words = content.words || ['the', 'and', 'is', 'it', 'a'];
      words.slice(0, 3).forEach(word => {
        const letters = word.toUpperCase().split('').join('-');
        questions.push({
          question: `Spell the word: ${word.toUpperCase()}`,
          options: [letters, letters.split('').reverse().join('-'), 'X-Y-Z', 'A-B-C'],
          answer: letters,
          hint: 'Sound it out!'
        });
      });
    }
    
    // Phonics/Nonsense words
    else if (subject === 'phonics') {
      const words = content.words || ['zim', 'vop', 'baf'];
      words.slice(0, 3).forEach(word => {
        questions.push({
          question: `Read this word: ${word.toUpperCase()}`,
          options: [word, word.split('').reverse().join(''), word.replace(word[0], 'x'), word + 's'],
          answer: word,
          hint: 'Sound out each letter!'
        });
      });
    }
    
    // Reading activities (EL Education)
    else if (subject === 'reading') {
      if (content.focus === 'rhyming') {
        questions.push(
          { question: 'What rhymes with CAT?', options: ['Dog', 'Hat', 'Sun', 'Run'], answer: 'Hat', hint: 'Same ending sound!' },
          { question: 'What rhymes with MOON?', options: ['Star', 'Spoon', 'Fish', 'Sun'], answer: 'Spoon', hint: '-OON sounds!' },
          { question: 'What rhymes with TREE?', options: ['Leaf', 'Bee', 'Bird', 'Green'], answer: 'Bee', hint: '-EE sounds!' }
        );
      } else if (content.focus === 'comprehension') {
        questions.push(
          { question: 'What happens FIRST in a story?', options: ['Beginning', 'Middle', 'End'], answer: 'Beginning', hint: 'Stories start here!' },
          { question: 'Characters are the...', options: ['People in a story', 'Words', 'Pictures'], answer: 'People in a story', hint: 'Who is the story about?' },
          { question: 'The setting is...', options: ['Where it happens', 'Who', 'When'], answer: 'Where it happens', hint: 'The place!' }
        );
      } else {
        questions.push(
          { question: 'What sound does "B" make?', options: ['/b/', '/d/', '/p/'], answer: '/b/', hint: 'Ball starts with B!' },
          { question: 'CAT has how many sounds?', options: ['2', '3', '4'], answer: '3', hint: 'C-A-T' },
          { question: 'What letter makes the /s/ sound?', options: ['S', 'Z', 'C'], answer: 'S', hint: 'Snake!' }
        );
      }
    }
    
    // Math activities (Envision)
    else if (subject === 'math') {
      if (content.focus === 'counting' || content.type === 'counting') {
        questions.push(
          { question: 'What comes after 5?', options: ['4', '6', '7'], answer: '6', hint: '5, then...' },
          { question: 'Count: 1, 2, 3, _', options: ['4', '5', '3'], answer: '4', hint: 'Keep counting!' },
          { question: 'How many fingers on TWO hands?', options: ['5', '8', '10'], answer: '10', hint: '5 + 5' }
        );
      } else if (content.focus === 'addition_concept') {
        questions.push(
          { question: '2 + 2 = ?', options: ['3', '4', '5'], answer: '4', hint: 'Count them together!' },
          { question: '3 + 1 = ?', options: ['3', '4', '5'], answer: '4', hint: 'Add one more!' },
          { question: '1 + 1 = ?', options: ['1', '2', '3'], answer: '2', hint: 'One and one!' }
        );
      } else {
        questions.push(
          { question: 'Which is more: 5 or 3?', options: ['5', '3'], answer: '5', hint: 'Count them!' },
          { question: '4 - 1 = ?', options: ['2', '3', '4'], answer: '3', hint: 'Take one away!' },
          { question: 'What shape has 4 equal sides?', options: ['Circle', 'Square', 'Triangle'], answer: 'Square', hint: 'All sides the same!' }
        );
      }
    }
    
    // Christian activities
    else if (subject === 'christian') {
      if (content.story === 'creation') {
        questions.push(
          { question: 'Who made the world?', options: ['God', 'People', 'Animals'], answer: 'God', hint: 'In the beginning...' },
          { question: 'How many days did creation take?', options: ['5', '6', '7'], answer: '7', hint: 'Including rest day!' },
          { question: 'What did God make on day 1?', options: ['Light', 'Animals', 'People'], answer: 'Light', hint: 'Let there be light!' }
        );
      } else if (content.story === 'noah') {
        questions.push(
          { question: 'What did Noah build?', options: ['House', 'Ark', 'Tower'], answer: 'Ark', hint: 'A big boat!' },
          { question: 'How many of each animal?', options: ['One', 'Two', 'Three'], answer: 'Two', hint: 'A pair!' },
          { question: 'What appeared after the flood?', options: ['Rain', 'Rainbow', 'Snow'], answer: 'Rainbow', hint: 'Colors in the sky!' }
        );
      } else {
        questions.push(
          { question: 'Jesus loves...', options: ['Some people', 'Everyone', 'Only good people'], answer: 'Everyone', hint: 'All of us!' },
          { question: 'The Bible is God\'s...', options: ['Book', 'Word', 'Story'], answer: 'Word', hint: 'He speaks to us!' },
          { question: 'We talk to God through...', options: ['Prayer', 'TV', 'Phone'], answer: 'Prayer', hint: 'Close your eyes...' }
        );
      }
    }
    
    // Science activities
    else if (subject === 'science') {
      questions.push(
        { question: 'What do plants need to grow?', options: ['Water', 'Toys', 'Books'], answer: 'Water', hint: 'Plants get thirsty!' },
        { question: 'The sun is a...', options: ['Planet', 'Star', 'Moon'], answer: 'Star', hint: 'It gives us light!' },
        { question: 'Animals that eat plants are...', options: ['Herbivores', 'Carnivores', 'Fish'], answer: 'Herbivores', hint: 'Herb = plant!' }
      );
    }
    
    // Default questions for any other subject
    else {
      questions.push(
        { question: 'Are you ready to learn?', options: ['Yes!', 'Maybe'], answer: 'Yes!', hint: 'Learning is fun!' },
        { question: 'What is 1 + 1?', options: ['1', '2', '3'], answer: '2', hint: 'Count them!' },
        { question: 'The sky is usually...', options: ['Blue', 'Green', 'Red'], answer: 'Blue', hint: 'Look up!' }
      );
    }
    
    return questions;
  };

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript.toLowerCase().trim();
        handleVoiceAnswer(transcript);
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  };

  const handleVoiceAnswer = (transcript) => {
    const questions = activity?.questions || activity?.content?.problems || [];
    const current = questions[currentQuestion];
    if (!current) return;
    
    const options = current.options;
    const matchedOption = options.find(opt => 
      transcript.includes(opt.toString().toLowerCase())
    );
    
    if (matchedOption) {
      handleAnswer(matchedOption.toString());
    } else {
      toast.info("I didn't catch that. Try again or tap an answer!");
    }
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error('Voice input not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const speak = (text) => {
    _speakTTS(text, 0.9);
  };

  const handleAnswer = (answer) => {
    const questions = activity?.questions || activity?.content?.problems || [];
    const current = questions[currentQuestion];
    const correctAnswer = current.answer || current.correct_answer;
    const isCorrect = answer === correctAnswer;
    
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    if (isCorrect) {
      setScore(score + 1);
      speak("Great job! That's correct!");
    } else {
      speak(`Not quite. The answer was ${correctAnswer}.`);
    }
    
    setAnswers([...answers, { question: currentQuestion, answer, isCorrect }]);
    
    // Move to next question after delay
    setTimeout(() => {
      setShowFeedback(false);
      setSelectedAnswer(null);
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        finishActivity();
      }
    }, 1500);
  };

  const finishActivity = async () => {
    const questions = activity?.questions || activity?.content?.problems || [];
    const finalScore = Math.round((score / questions.length) * 100);
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    
    setShowResult(true);
    
    if (finalScore >= 70) {
      setShowConfetti(true);
      speak("Amazing! You did great!");
      setTimeout(() => setShowConfetti(false), 5000);
    }
    
    // Submit results to API
    if (childId) {
      try {
        await api.post(`/activities/${activityId}/complete?child_id=${childId}&score=${finalScore}&time_spent=${timeSpent}`);
        // Also log Kiddom attempt for anti-cheat / study-history rounds counter.
        if (isKmath) {
          try {
            await api.post('/kmath/attempts', {
              activity_id: activityId,
              child_id: childId,
              correct_count: Math.round((finalScore / 100) * (activity?.questions?.length || 0)),
              total_count: activity?.questions?.length || 0,
              reshuffle: false,
            });
            const nxt = await api.get(`/kmath/next-skill/${childId}`);
            if (nxt?.data?.suggest_next && nxt.data.next_activity_id) {
              toast.success(
                `Mastered "${nxt.data.current_skill}"! Try "${nxt.data.next_activity_title}" next.`,
                {
                  action: {
                    label: 'Open',
                    onClick: () => { window.location.href = `/activity/${nxt.data.next_activity_id}?child=${childId}`; },
                  },
                  duration: 12000,
                }
              );
            }
          } catch (_e) { /* non-fatal */ }
        }
      } catch (error) {
        console.error('Failed to save progress:', error);
      }
    }
  };

  const handleDragDropComplete = async (finalScore, attempts) => {
    setShowConfetti(true);
    setScore(Math.round(finalScore / 10)); // Convert to answer count style
    setShowResult(true);
    
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    
    if (childId) {
      try {
        await api.post(`/activities/${activityId}/complete?child_id=${childId}&score=${finalScore}&time_spent=${timeSpent}`);
      } catch (error) {
        console.error('Failed to save progress:', error);
      }
    }
  };

  // Check if this is a drag-drop or matching activity
  const isDragDropActivity = activity?.activity_type === 'drag_drop' || activity?.activity_type === 'matching';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-100 to-white">
        <Loader2 className="w-12 h-12 animate-spin text-sky-500" />
      </div>
    );
  }

  // Render drag-drop game for appropriate activity types
  if (isDragDropActivity && !showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white theme-kids">
        {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
        
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-purple-200 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <Link to={childId ? `/learn/${childId}` : '/dashboard'}>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <h1 className="font-fredoka text-lg font-bold text-purple-800">{activity?.title}</h1>
              <div className="w-10" />
            </div>
          </div>
        </header>

        {/* Drag Drop Game */}
        <main className="max-w-3xl mx-auto px-4 py-8">
          <DragDropGame
            activity={activity}
            onComplete={handleDragDropComplete}
            childId={childId}
          />
        </main>
      </div>
    );
  }

  const questions = activity?.questions || activity?.content?.problems || [];
  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (showResult) {
    const finalScore = Math.round((score / questions.length) * 100);
    const passed = finalScore >= 70;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white flex items-center justify-center p-4">
        {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
        
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8">
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${passed ? 'bg-green-100' : 'bg-orange-100'}`}>
                {passed ? (
                  <Trophy className="w-12 h-12 text-yellow-500" />
                ) : (
                  <Star className="w-12 h-12 text-orange-500" />
                )}
              </div>
              
              <h2 className="font-fredoka text-3xl font-bold text-slate-800 mb-2">
                {passed ? 'Amazing Job!' : 'Good Try!'}
              </h2>
              
              <p className="text-xl text-slate-600 mb-4">
                You scored <span className="font-bold text-sky-500">{finalScore}%</span>
              </p>
              
              <p className="text-slate-500 mb-6">
                {score} out of {questions.length} correct
              </p>
              
              <div className="flex flex-col gap-3">
                {childId && (
                  <Link to={`/learn/${childId}`}>
                    <Button className="w-full h-14 text-lg bg-sky-500 hover:bg-sky-600" data-testid="back-to-hub-btn">
                      <Home className="w-5 h-5 mr-2" /> Back to Learning Hub
                    </Button>
                  </Link>
                )}
                <Button
                  variant="outline"
                  className="w-full h-14 text-lg"
                  onClick={() => window.location.reload()}
                  data-testid="try-again-btn"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white theme-kids">
      {/* Always-visible exit + pause controls (kids never get stuck) */}
      <PracticeExitControls childId={childId} />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-sky-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to={childId ? `/learn/${childId}` : '/dashboard'}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="font-fredoka font-bold text-lg text-slate-800 truncate max-w-[60%] text-center">
              {activity?.title || 'Activity'}
            </h1>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <span className="font-bold">{score}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white py-2 px-4 border-b border-sky-100">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-600">
              {currentQuestion + 1} / {questions.length}
            </span>
            <Progress value={progress} className="flex-1 h-3" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Zearn-style Learn → Fluency → Independent phase bar (Kiddom Math only) */}
        {isKmath && (
          <div className="mb-6" data-testid="zearn-phase-bar">
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { key: 'learn', label: 'Learn', color: 'bg-purple-500', icon: '📺' },
                { key: 'fluency', label: 'Fluency', color: 'bg-amber-500', icon: '⚡' },
                { key: 'independent', label: 'Independent', color: 'bg-emerald-500', icon: '✍️' },
              ].map((p) => {
                const active = zearnPhase === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setZearnPhase(p.key)}
                    className={`p-3 rounded-xl border-2 text-sm font-bold transition-all ${
                      active
                        ? `${p.color} text-white border-transparent shadow-md`
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`zearn-phase-${p.key}`}
                  >
                    <span className="mr-1">{p.icon}</span>
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* LEARN PHASE — video + concept from zearn-flow endpoint */}
            {zearnPhase === 'learn' && (
              <Card className="border-purple-200 bg-purple-50/40 mb-6" data-testid="zearn-learn-panel">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📺</span>
                    <h3 className="font-fredoka text-lg font-bold text-slate-800">
                      {zearnFlow?.phases?.learn?.label || 'Learn'}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    {zearnFlow?.phases?.learn?.description || 'Watch the mini-lesson, then read the key concept.'}
                  </p>
                  {(zearnFlow?.phases?.learn?.video_urls || []).map((url, i) => (
                    <div key={i} className="aspect-video rounded-lg overflow-hidden bg-black">
                      <iframe
                        src={url.includes('embed') ? url : url.replace('watch?v=', 'embed/')}
                        title={`Lesson video ${i + 1}`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ))}
                  {!zearnFlow?.phases?.learn?.video_urls?.length && (
                    <p className="text-xs text-slate-500 italic">
                      No lesson video linked for this activity — read the concept below and try Fluency practice.
                    </p>
                  )}
                  {zearnFlow?.phases?.learn?.concept && (() => {
                    const conceptText = zearnFlow.phases.learn.concept;
                    const sentences = splitSentences(conceptText);
                    return (
                    <div className="bg-white rounded-lg p-4 border border-slate-200 text-sm text-slate-700" data-testid="zearn-learn-concept">
                      {sentences.length === 0 ? (
                        <p className="whitespace-pre-line">{conceptText}</p>
                      ) : (
                        <p className="leading-relaxed">
                          {sentences.map((s, i) => {
                            const isActive = i === kmathSentIdx;
                            if (!isActive) {
                              return (
                                <span key={i} data-testid={`kmath-concept-sentence-${i}`}>
                                  {s}{i < sentences.length - 1 ? ' ' : ''}
                                </span>
                              );
                            }
                            const tokens = splitWords(s);
                            return (
                              <span
                                key={i}
                                data-testid={`kmath-concept-sentence-${i}`}
                                className="bg-amber-100 rounded-md shadow-inner ring-2 ring-amber-200 px-1 py-0.5"
                              >
                                {tokens.map((tok, wi) => {
                                  const isWordActive = wi === kmathWordIdx;
                                  return (
                                    <span
                                      key={wi}
                                      data-testid={isWordActive ? 'kmath-concept-word-active' : undefined}
                                      className={isWordActive ? 'bg-purple-500 text-white rounded px-0.5' : ''}
                                    >
                                      {tok}
                                    </span>
                                  );
                                })}
                                {i < sentences.length - 1 ? ' ' : ''}
                              </span>
                            );
                          })}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <button
                          type="button"
                          onClick={cycleKmathSpeed}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full border-2 border-purple-200 text-purple-700 text-xs hover:bg-purple-50"
                          aria-label={`Reading speed: ${SPEED_PRESETS[kmathReadSpeed].label}. Tap to change.`}
                          title={`Reading speed: ${SPEED_PRESETS[kmathReadSpeed].label}. Tap to cycle slow / normal / fast.`}
                          data-testid="kmath-read-speed"
                        >
                          {kmathReadSpeed === 'slow' ? (
                            <Turtle className="w-3 h-3" />
                          ) : kmathReadSpeed === 'normal' ? (
                            <Rabbit className="w-3 h-3" />
                          ) : (
                            <><Rabbit className="w-3 h-3" /><Zap className="w-2.5 h-2.5 -ml-1 text-orange-500" /></>
                          )}
                          <span>{SPEED_PRESETS[kmathReadSpeed].label}</span>
                        </button>
                        {isKmathReading ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 text-xs h-7"
                            onClick={stopKmathConcept}
                            data-testid="kmath-concept-tts-stop"
                          >
                            <VolumeX className="w-3 h-3 mr-1" /> Stop
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-xs h-7"
                            onClick={() => playKmathConcept(conceptText)}
                            data-testid="kmath-concept-tts-play"
                          >
                            <Volume2 className="w-3 h-3 mr-1" /> Read to me
                          </Button>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                  <Button
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={() => setZearnPhase('fluency')}
                    data-testid="zearn-next-fluency"
                  >
                    Ready for Fluency Practice →
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* FLUENCY PHASE — 60-second countdown overlay */}
            {zearnPhase === 'fluency' && (
              <Card className="border-amber-300 bg-amber-50 mb-6" data-testid="zearn-fluency-panel">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">⚡</span>
                    <div>
                      <p className="font-bold text-slate-800">Fluency warm-up · {fluencyRemaining}s</p>
                      <p className="text-xs text-slate-600">
                        Answer as many as you can in 60 seconds — speed matters here!
                      </p>
                    </div>
                  </div>
                  <div className="text-4xl font-extrabold text-amber-700 tabular-nums" data-testid="zearn-fluency-timer">
                    {fluencyRemaining}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Hide questions on Learn phase (kmath only) */}
        {!(isKmath && zearnPhase === 'learn') && (
        <AnimatePresence mode="wait">
          {isKmath && (
            <div className="flex justify-end mb-3">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  // Log this round's tally before we reshuffle.
                  try {
                    const correctSoFar = (activity?.questions || []).reduce((n, _q, i) => {
                      // simple heuristic: currentQuestion is the pointer past answered ones
                      return i < currentQuestion ? n + 1 : n;
                    }, 0);
                    await api.post('/kmath/attempts', {
                      activity_id: activityId,
                      child_id: childId,
                      correct_count: correctSoFar,
                      total_count: (activity?.questions || []).length,
                      reshuffle: true,
                    });
                    // Auto-suggest next skill if the child has been crushing this one.
                    try {
                      const nxt = await api.get(`/kmath/next-skill/${childId}`);
                      if (nxt?.data?.suggest_next && nxt.data.next_activity_id) {
                        toast.success(
                          `You've mastered this! Try "${nxt.data.next_activity_title}" next.`,
                          {
                            action: {
                              label: 'Open',
                              onClick: () => { window.location.href = `/activity/${nxt.data.next_activity_id}?child=${childId}`; },
                            },
                            duration: 10000,
                          }
                        );
                      }
                    } catch (_e) { /* non-fatal */ }
                  } catch (_e) { /* non-fatal */ }

                  const fresh = generateKmathQuestions(activity, activity?.questions?.length || 5);
                  setActivity((a) => ({ ...a, questions: fresh }));
                  setCurrentQuestion(0);
                  setSelectedAnswer(null);
                  setShowFeedback(false);
                  toast.success('Fresh questions — good luck!');
                }}
                data-testid="kmath-new-questions-btn"
                title="Reshuffle to a new set of questions"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> New questions
              </Button>
            </div>
          )}
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onAnimationComplete={() => { if (currentQ?.question) speak(currentQ.question); }}
          >
            <Card className="kids-card mb-6">
              <CardContent className="p-8">
                {/* Question */}
                <div className="text-center mb-8">
                  {currentQ?.visual && (
                    <span className="text-5xl mb-3 block">{currentQ.visual}</span>
                  )}
                  <h2 className="font-fredoka text-2xl md:text-3xl font-bold text-slate-800 mb-4">
                    {currentQ?.question}
                  </h2>
                  
                  {/* Always show Listen button for Pre-K accessibility */}
                  {(
                    <Button
                      variant="ghost"
                      onClick={() => speak(currentQ?.question)}
                      className="mb-2"
                      data-testid="tts-question-btn"
                    >
                      <Volume2 className="w-5 h-5 mr-2" /> Listen
                    </Button>
                  )}
                </div>

                {/* Options */}
                <div className="grid grid-cols-2 gap-4">
                  {currentQ?.options?.map((option, index) => {
                    const isSelected = selectedAnswer === option.toString();
                    const correctAnswer = currentQ.answer || currentQ.correct_answer;
                    const isCorrect = option.toString() === correctAnswer;
                    
                    let buttonClass = 'h-20 text-xl font-bold rounded-2xl border-4 transition-all ';
                    
                    if (showFeedback) {
                      if (isCorrect) {
                        buttonClass += 'bg-green-100 border-green-400 text-green-700';
                      } else if (isSelected && !isCorrect) {
                        buttonClass += 'bg-red-100 border-red-400 text-red-700';
                      } else {
                        buttonClass += 'bg-slate-100 border-slate-200 text-slate-400';
                      }
                    } else {
                      buttonClass += 'bg-white border-sky-200 hover:border-sky-400 hover:bg-sky-50 text-slate-700';
                    }
                    
                    return (
                      <Button
                        key={index}
                        className={buttonClass}
                        onClick={() => !showFeedback && handleAnswer(option.toString())}
                        disabled={showFeedback}
                        data-testid={`option-${index}`}
                      >
                        {showFeedback && isCorrect && <Check className="w-6 h-6 mr-2" />}
                        {showFeedback && isSelected && !isCorrect && <X className="w-6 h-6 mr-2" />}
                        {option}
                      </Button>
                    );
                  })}
                </div>

                {/* Hint */}
                {currentQ?.hint && (
                  <p className="text-center text-slate-500 mt-6 text-sm">
                    💡 Hint: {currentQ.hint}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Voice Input */}
            {child?.accessibility_settings?.voice_enabled && (
              <div className="text-center">
                <Button
                  size="lg"
                  className={`h-16 w-16 rounded-full ${isListening ? 'bg-red-500 hover:bg-red-600 voice-active' : 'bg-sky-500 hover:bg-sky-600'}`}
                  onClick={toggleListening}
                  data-testid="voice-input-btn"
                >
                  {isListening ? (
                    <MicOff className="w-8 h-8" />
                  ) : (
                    <Mic className="w-8 h-8" />
                  )}
                </Button>
                <p className="text-sm text-slate-500 mt-2">
                  {isListening ? 'Listening... Say your answer!' : 'Tap to speak your answer'}
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        )}
      </main>
    </div>
  );
};

export default ActivityPage;
